// T-203 — UN composant de suivi des actions (notes datées, prochaine action avec échéance et assigné) posé sur les fiches prospect et client.
// Le serveur décide de tout (tenant de la session, droits, capacité) ; ce composant affiche et envoie. Types d'action : liste éditable `crm_types_action`.
// Charte : « Planifier une action » = bouton primaire (1 par écran), « Ajouter une note » = secondaire ; tokens --oh-* ; sentence case.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import ChampDate from "../ui/champ-date";
import { useConfigList } from "../../hooks/useConfigList";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;
export const ETATS = { a_faire: ["À faire", "amber"], faite: ["Faite", "green"], annulee: ["Annulée", "red"] };

export const aujourdhuiIso = () => new Date().toLocaleDateString("sv-SE");              // AAAA-MM-JJ, jour local
export const estEnRetard = (a, jour = aujourdhuiIso()) => a?.nature === "action" && a?.etat === "a_faire" && !!a?.echeance && a.echeance < jour;
export const formatEcheance = (a) => {
  if (!a?.echeance) return "sans échéance";
  const [y, m, d] = a.echeance.split("-");
  return `${d}/${m}/${y}${a.heure ? ` à ${a.heure}` : ""}`;
};
const formatDateHeure = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso || ""; } };
/** Échéance proposée par défaut : aujourd'hui + délai du type (ex. relance : 2 jours), sinon vide. */
export const echeanceParDefaut = (type, jour = aujourdhuiIso()) => {
  if (!type || type.delai_defaut_jours == null || type.delai_defaut_jours === "") return "";
  const d = new Date(`${jour}T12:00:00`); d.setDate(d.getDate() + Number(type.delai_defaut_jours));
  return d.toLocaleDateString("sv-SE");
};

function Pastille({ etat }) {
  const [label, c] = ETATS[etat] || [etat, "blue"];
  return <span className="text-xs px-2 py-0.5" data-testid={`action-etat-${etat}`}
    style={{ background: `var(--oh-stat-${c}-bg)`, color: `var(--oh-stat-${c}-text)`, border: `1px solid var(--oh-stat-${c}-border)`, borderRadius: "var(--oh-radius-control, 8px)" }}>{label}</span>;
}

export default function SuiviActions({ cibleType, cibleId, tenantId, peutAssigner = false }) {
  const { items: types } = useConfigList("crm_types_action");
  const typesActifs = useMemo(() => (types || []).filter((t) => t.actif !== false).sort((a, b) => (a.ordre || 0) - (b.ordre || 0)), [types]);
  const [data, setData] = useState({ actions: [], prochaine_action: null });
  const [erreur, setErreur] = useState(null);
  const [form, setForm] = useState(null);                       // null | { nature: "note"|"action", type_action, texte, echeance, heure, assigne_a }
  const [envoi, setEnvoi] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);

  const entetes = useCallback(() => {
    const tok = localStorage.getItem("session_token");
    return { "Content-Type": "application/json", ...enteteTenant(tenantId), ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
  }, [tenantId]);

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/crm/actions?cible_type=${encodeURIComponent(cibleType)}&cible_id=${encodeURIComponent(cibleId)}`, { headers: entetes(), credentials: "include" });
      if (r.ok) { setData(await r.json()); setErreur(null); }
      else setErreur(r.status === 403 ? "Le suivi des actions n'est pas activé pour cet espace." : "Chargement du suivi impossible");
    } catch { setErreur("Erreur réseau"); }
  }, [cibleType, cibleId, entetes]);
  useEffect(() => { if (cibleId) charger(); }, [charger, cibleId]);
  useEffect(() => {
    if (!peutAssigner) return;
    fetch(`${API}/api/regie/admin/commerciaux`, { headers: entetes(), credentials: "include" })
      .then((r) => (r.ok ? r.json() : { commerciaux: [] })).then((d) => setCommerciaux(d.commerciaux || [])).catch(() => setCommerciaux([]));
  }, [peutAssigner, entetes]);

  const ouvrir = (nature) => {
    const type = typesActifs.find((t) => (nature === "note" ? t.nature_par_defaut === "note" : t.nature_par_defaut === "action")) || typesActifs[0];
    setForm({ nature, type_action: type?.id || "", texte: "", echeance: nature === "action" ? echeanceParDefaut(type) : "", heure: "", assigne_a: "" });
  };
  const changerType = (id) => {
    const t = typesActifs.find((x) => x.id === id);
    setForm((f) => ({ ...f, type_action: id, echeance: f.nature === "action" ? (echeanceParDefaut(t) || f.echeance) : "" }));
  };
  const typeCourant = typesActifs.find((t) => t.id === form?.type_action);

  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      const corps = { cible_type: cibleType, cible_id: cibleId, nature: form.nature, type_action: form.type_action, texte: form.texte,
        ...(form.nature === "action" ? { echeance: form.echeance || undefined, heure: form.heure || undefined, assigne_a: form.assigne_a || undefined } : {}) };
      const r = await fetch(`${API}/api/crm/actions`, { method: "POST", headers: entetes(), credentials: "include", body: JSON.stringify(corps) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(typeof d.detail === "string" ? d.detail : "Enregistrement impossible"); return; }
      toast.success(form.nature === "note" ? "Note ajoutée" : "Action planifiée");
      setForm(null); charger();
    } catch { toast.error("Erreur réseau"); } finally { setEnvoi(false); }
  };
  const clore = async (a, geste) => {
    try {
      const r = await fetch(`${API}/api/crm/actions/${a.id}/${geste}`, { method: "POST", headers: entetes(), credentials: "include", body: JSON.stringify({}) });
      if (r.ok) { toast.success(geste === "faire" ? "Action faite" : "Action annulée"); charger(); } else toast.error("Opération impossible");
    } catch { toast.error("Erreur réseau"); }
  };

  const libelleType = (id) => (types || []).find((t) => t.id === id)?.label || id;
  const p = data.prochaine_action;
  const champ = { background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)", borderRadius: 8, height: 36, padding: "0 12px", fontSize: 14, colorScheme: "light dark" };

  return (
    <section className="space-y-3" data-testid="suivi-actions" aria-label="Suivi des actions">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Suivi</h3>
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => ouvrir("note")} data-testid="suivi-ajouter-note"
            style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" }}>Ajouter une note</Button>
          <Button type="button" size="sm" onClick={() => ouvrir("action")} data-testid="suivi-planifier">Planifier une action</Button>
        </div>
      </div>

      {erreur && <p className="text-xs" role="alert" style={{ color: "var(--oh-error-text)" }}>{erreur}</p>}

      {p && (
        <div className="rounded-lg px-3 py-2" data-testid="suivi-prochaine"
          style={{ background: estEnRetard(p) ? "var(--oh-error-bg)" : "var(--oh-info-bg)", color: estEnRetard(p) ? "var(--oh-error-text)" : "var(--oh-info-text)",
                   border: `1px solid ${estEnRetard(p) ? "var(--oh-error-border)" : "var(--oh-info-border)"}` }}>
          <span className="text-xs">Prochaine action{estEnRetard(p) ? " (en retard)" : ""}</span>
          <div className="text-sm font-medium">{libelleType(p.type_action)} · {formatEcheance(p)}</div>
          {p.texte && <div className="text-xs">{p.texte}</div>}
        </div>
      )}

      {form && (
        <form onSubmit={envoyer} className="rounded-lg p-3 space-y-2" data-testid="suivi-formulaire" style={{ background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)" }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Type
              <select data-testid="suivi-type" value={form.type_action} onChange={(e) => changerType(e.target.value)} style={{ ...champ, width: "100%" }}>
                {typesActifs.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select></label>
            {form.nature === "action" && (<>
              <label className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Échéance{typeCourant?.demande_echeance ? " *" : ""}
                <ChampDate data-testid="suivi-echeance" value={form.echeance} onChange={(e) => setForm((f) => ({ ...f, echeance: e.target.value }))} style={{ ...champ, width: "100%" }} /></label>
              <label className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Heure
                <input data-testid="suivi-heure" type="time" value={form.heure} onChange={(e) => setForm((f) => ({ ...f, heure: e.target.value }))} style={{ ...champ, width: "100%" }} /></label>
            </>)}
          </div>
          {form.nature === "action" && peutAssigner && commerciaux.length > 0 && (
            <label className="text-xs block" style={{ color: "var(--oh-text-muted)" }}>Assigner à
              <select data-testid="suivi-assigne" value={form.assigne_a} onChange={(e) => setForm((f) => ({ ...f, assigne_a: e.target.value }))} style={{ ...champ, width: "100%" }}>
                <option value="">Moi / commercial de la fiche</option>
                {commerciaux.map((c) => <option key={c.user_id} value={c.user_id}>{c.name || c.email}</option>)}
              </select></label>
          )}
          <label className="text-xs block" style={{ color: "var(--oh-text-muted)" }}>{form.nature === "note" ? "Note" : "Détail"}
            <textarea data-testid="suivi-texte" rows={3} value={form.texte} onChange={(e) => setForm((f) => ({ ...f, texte: e.target.value }))}
              style={{ ...champ, width: "100%", height: "auto", padding: 8 }} maxLength={4000} /></label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setForm(null)}>Annuler</Button>
            <Button type="submit" size="sm" disabled={envoi} data-testid="suivi-enregistrer">{form.nature === "note" ? "Enregistrer la note" : "Planifier"}</Button>
          </div>
        </form>
      )}

      <ul className="space-y-2" data-testid="suivi-liste">
        {data.actions.length === 0 && !erreur && <li className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Aucune note ni action pour le moment.</li>}
        {data.actions.map((a) => (
          <li key={a.id} className="rounded-lg px-3 py-2" data-testid={`suivi-ligne-${a.nature}`} style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)" }}>
            <div className="flex items-center gap-2 flex-wrap text-xs" style={{ color: "var(--oh-text-muted)" }}>
              <span style={{ color: "var(--oh-text)" }} className="font-medium">{a.nature === "note" ? "Note" : "Action"} · {libelleType(a.type_action)}</span>
              {a.nature === "action" && <><Pastille etat={a.etat} /><span>échéance {formatEcheance(a)}</span></>}
              <span className="ml-auto">{formatDateHeure(a.cree_le)}</span>
            </div>
            {a.texte && <p className="text-sm mt-1" style={{ color: "var(--oh-text)" }}>{a.texte}</p>}
            {a.resultat && <p className="text-xs mt-1" style={{ color: "var(--oh-text-secondary)" }}>Résultat : {a.resultat}</p>}
            {a.nature === "action" && a.etat === "a_faire" && (
              <div className="flex gap-2 mt-2">
                <Button type="button" size="sm" variant="outline" onClick={() => clore(a, "faire")} data-testid="suivi-faite">Marquer faite</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => clore(a, "annuler")} data-testid="suivi-annuler">Annuler</Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
